import {PureComponent} from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {X} from 'react-feather'

import api from '../../../../../../api.js'
import conf from '../../../../../conf.js'
import withManagers from '../../../../../HOC/withManagers.jsx'
import {availabilityChoices} from '../../../../../../shared/components/FilterBar/components/FilterAvailability/FilterAvailability.jsx'

import {
  AVAILABILITY_FILTER_KEY,
  CITY_FILTER_KEY,
  DISTANCE_FILTER_KEY,
  IT_SKILLS_FILTER_KEY,
  LANGUAGE_FILTER_KEY,
  MANAGER_FILTER_KEY,
  NB_YEAR_OF_EXPERIENCE_FILTER_KEY,
  PRICE_MAX_FILTER_KEY,
  PRICE_MIN_FILTER_KEY,
  RELOCATION_FILTER_KEY,
  SECTEUR_FILTER_KEY,
  UNIVERSE_FILTER_KEY,
} from '../../../../../../shared/constants/filters.js'

import './FreelanceSearchActiveFilters.scss'

const GOLD_TOPOLOGY_NAME = 'gold'

class FreelanceSearchActiveFilters extends PureComponent {
  static propTypes = {
    isAdmin: PropTypes.bool,
    isItSkills: PropTypes.bool,
    isLanguages: PropTypes.bool,
    isSecteurs: PropTypes.bool,
    managers: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    requirementChoices: PropTypes.array,
    searchQuery: PropTypes.object,
    secteurs: PropTypes.array,
    universes: PropTypes.array,
  }

  static defaultProps = {
    isAdmin: false,
    isItSkills: false,
    isLanguages: false,
    isSecteurs: false,
    managers: [],
    requirementChoices: [],
    searchQuery: {},
    secteurs: [],
    universes: [],
  }

  state = {
    languageLabel: null,
  }

  componentDidMount() {
    this.resolveLanguageLabel()
  }

  componentDidUpdate(prevProps) {
    const prevLanguages = prevProps.searchQuery?.[LANGUAGE_FILTER_KEY]
    const currentLanguages = this.props.searchQuery?.[LANGUAGE_FILTER_KEY]
    if (JSON.stringify(prevLanguages) !== JSON.stringify(currentLanguages)) {
      this.resolveLanguageLabel()
    }
  }

  resolveLanguageLabel = async () => {
    const languageId = this.props.searchQuery?.[LANGUAGE_FILTER_KEY]?.[0]?.split(':')?.[0]
    if (!languageId) {
      this.setState({languageLabel: null})
      return
    }

    try {
      const name = (await api.languages.name({languageId})).data
      this.setState({languageLabel: name ?? null})
    } catch (error) {
      console.error({error})
    }
  }

  removeFilter = (keys) => {
    const data = this.props.searchQuery
    ;[].concat(keys).forEach((key) => delete data[key])
    this.props.onChange(data)
  }

  removeArrayValue = (key, value) => {
    const data = this.props.searchQuery
    const remaining = (data[key] || []).filter((item) => item !== value)
    if (remaining.length) {
      data[key] = remaining
    } else {
      delete data[key]
    }
    this.props.onChange(data)
  }

  removeKeyword = (keyword) => {
    const data = this.props.searchQuery
    const remaining = (data.keywords || []).filter((item) => item !== keyword)
    if (remaining.length) {
      data.keywords = remaining
    } else {
      delete data.keywords
      delete data.requirements
    }
    this.props.onChange(data)
  }

  getChips = () => {
    const {isAdmin, isItSkills, isLanguages, isSecteurs, managers, requirementChoices, searchQuery, secteurs, universes} = this.props
    const effective = searchQuery ?? {}
    const chips = []

    if (effective.keywords?.length) {
      const requirementValue = effective.requirements ? parseInt(effective.requirements, 10) : null
      const requirementChoice = requirementValue ? requirementChoices.find((choice) => choice.value === requirementValue) : null

      effective.keywords.forEach((keyword) => {
        chips.push({
          key: `keyword-${keyword}`,
          label: requirementChoice ? `${requirementChoice.text} : ${keyword}` : keyword,
          onRemove: () => this.removeKeyword(keyword),
        })
      })
    }

    if (effective[UNIVERSE_FILTER_KEY]) {
      const universe = universes.find((item) => Number(item.id) === Number(effective[UNIVERSE_FILTER_KEY]))
      chips.push({
        key: 'universe',
        label: universe?.name ?? 'Univers',
        removable: false,
      })
    }

    conf.search.topologies.freelances
      .filter((topology) => topology.name !== GOLD_TOPOLOGY_NAME)
      .forEach((topology) => {
        const value = effective[topology.name]
        if (value === undefined || value === null || value === '') return

        chips.push({
          key: `topology-${topology.name}`,
          label: topology.labels[value === '1' ? 0 : 1],
          onRemove: () => this.removeFilter(topology.name),
        })
      })

    if (effective[CITY_FILTER_KEY]) {
      chips.push({
        key: 'city',
        label: effective[CITY_FILTER_KEY],
        onRemove: () => this.removeFilter([CITY_FILTER_KEY, 'latitude', 'longitude']),
      })
    }

    if (effective[RELOCATION_FILTER_KEY] === '1' || effective[RELOCATION_FILTER_KEY] === 1) {
      chips.push({
        key: 'relocation',
        label: 'Mobile partout en France',
        onRemove: () => this.removeFilter(RELOCATION_FILTER_KEY),
      })
    }

    if (effective[DISTANCE_FILTER_KEY]) {
      chips.push({
        key: 'distance',
        label: `${effective[DISTANCE_FILTER_KEY]} km`,
        onRemove: () => this.removeFilter(DISTANCE_FILTER_KEY),
      })
    }

    if (effective[AVAILABILITY_FILTER_KEY] !== undefined && effective[AVAILABILITY_FILTER_KEY] !== null && effective[AVAILABILITY_FILTER_KEY] !== '') {
      const choice = availabilityChoices.find((item) => item.value === Number(effective[AVAILABILITY_FILTER_KEY]))
      if (choice) {
        chips.push({
          key: 'availability',
          label: choice.text,
          onRemove: () => this.removeFilter(AVAILABILITY_FILTER_KEY),
        })
      }
    }

    if (isAdmin && effective[PRICE_MIN_FILTER_KEY]) {
      chips.push({
        key: 'priceMin',
        label: `TJM min : ${effective[PRICE_MIN_FILTER_KEY]}`,
        onRemove: () => this.removeFilter(PRICE_MIN_FILTER_KEY),
      })
    }

    if (isAdmin && effective[PRICE_MAX_FILTER_KEY]) {
      chips.push({
        key: 'priceMax',
        label: `TJM max : ${effective[PRICE_MAX_FILTER_KEY]}`,
        onRemove: () => this.removeFilter(PRICE_MAX_FILTER_KEY),
      })
    }

    (effective[NB_YEAR_OF_EXPERIENCE_FILTER_KEY] || []).forEach((xp) => {
      const experience = conf.search.experiences.find((item) => item.name === xp)
      chips.push({
        key: `xp-${xp}`,
        label: experience?.label ?? xp,
        onRemove: () => this.removeArrayValue(NB_YEAR_OF_EXPERIENCE_FILTER_KEY, xp),
      })
    })

    if (isSecteurs && effective[SECTEUR_FILTER_KEY]) {
      const secteur = secteurs.find((item) => Number(item.value) === Number(effective[SECTEUR_FILTER_KEY]))
      chips.push({
        key: 'secteur',
        label: secteur?.label ?? effective[SECTEUR_FILTER_KEY],
        onRemove: () => this.removeFilter(SECTEUR_FILTER_KEY),
      })
    }

    if (isLanguages && effective[LANGUAGE_FILTER_KEY]?.length) {
      chips.push({
        key: 'language',
        label: this.state.languageLabel ?? '…',
        onRemove: () => this.removeFilter(LANGUAGE_FILTER_KEY),
      })
    }

    if (isItSkills && effective[IT_SKILLS_FILTER_KEY]?.length) {
      chips.push({
        key: 'itSkills',
        label: effective[IT_SKILLS_FILTER_KEY][0],
        onRemove: () => this.removeFilter(IT_SKILLS_FILTER_KEY),
      })
    }

    if (effective[MANAGER_FILTER_KEY]) {
      const manager = managers.find((item) => Number(item.id) === Number(effective[MANAGER_FILTER_KEY]))
      chips.push({
        key: 'manager',
        label: manager?.fullname ?? effective[MANAGER_FILTER_KEY],
        onRemove: () => this.removeFilter(MANAGER_FILTER_KEY),
      })
    }

    return chips
  }

  render() {
    const chips = this.getChips()
    if (!chips.length) return null

    return (
      <div className="freelance-search-active-filters">
        {chips.map((chip) => (
          chip.removable === false ? (
            <span key={chip.key} className="freelance-search-active-filters__chip freelance-search-active-filters__chip--locked">
              {chip.label}
            </span>
          ) : (
            <button
              type="button"
              key={chip.key}
              className="freelance-search-active-filters__chip"
              onClick={chip.onRemove}
            >
              {chip.label}
              <X size={12} />
            </button>
          )
        ))}
      </div>
    )
  }
}

export default withManagers(connect(
  (state) => ({
    secteurs: state.secteurs.secteurs.map((secteur) => ({value: secteur.id, label: secteur.name})),
    universes: state.universe.universes,
  })
)(FreelanceSearchActiveFilters))
